// Readable capability catalog for the Team & access "Permissions" tab.
//
// The backend speaks in raw capability ids (`po.approve`, `ship.label.void`);
// ~100 areas × ~320 ids is unreadable as a flat list. This module folds every
// area (the id's first segment) into one of a dozen business groups and turns
// the remaining segments into words. Non-component module on purpose
// (react-refresh only-export-components). The backend test
// `test_team_capability_catalog_covers_backend.py` fails when a new backend
// area is not listed in AREAS below.

export type Locale = 'en' | 'tr'
type Text = { en: string; tr: string }

type CapabilityGroupId =
  | 'catalog'
  | 'orders'
  | 'stores'
  | 'pricing'
  | 'purchasing'
  | 'inventory'
  | 'shipping'
  | 'finance'
  | 'messaging'
  | 'drive'
  | 'team'
  | 'remote'
  | 'platform'
  | 'other'

const CAPABILITY_GROUPS: readonly { id: CapabilityGroupId; label: Text }[] = [
  { id: 'catalog', label: { en: 'Catalog & products', tr: 'Katalog ve ürünler' } },
  { id: 'orders', label: { en: 'Orders & returns', tr: 'Siparişler ve iadeler' } },
  { id: 'stores', label: { en: 'Stores & marketplaces', tr: 'Mağazalar ve pazar yerleri' } },
  { id: 'pricing', label: { en: 'Pricing & sales', tr: 'Fiyatlandırma ve satış' } },
  { id: 'purchasing', label: { en: 'Purchasing & suppliers', tr: 'Satın alma ve tedarikçiler' } },
  { id: 'inventory', label: { en: 'Warehouse & inventory', tr: 'Depo ve stok' } },
  { id: 'shipping', label: { en: 'Shipping', tr: 'Gönderim' } },
  { id: 'finance', label: { en: 'Finance & payments', tr: 'Finans ve ödemeler' } },
  { id: 'messaging', label: { en: 'Messaging & notifications', tr: 'Mesajlaşma ve bildirimler' } },
  { id: 'drive', label: { en: 'Files & Drive', tr: 'Dosyalar ve Drive' } },
  { id: 'team', label: { en: 'Team & people', tr: 'Ekip ve personel' } },
  { id: 'remote', label: { en: 'Servers & remote access', tr: 'Sunucular ve uzak erişim' } },
  { id: 'platform', label: { en: 'Platform & governance', tr: 'Platform ve yönetim' } },
  { id: 'other', label: { en: 'Other', tr: 'Diğer' } },
]

const a = (group: CapabilityGroupId, en: string, tr: string) => ({ group, label: { en, tr } })

/** Every backend capability area → business group + readable name. */
const AREAS: Record<string, { group: CapabilityGroupId; label: Text }> = {
  // Catalog & products
  catalog: a('catalog', 'Catalog', 'Katalog'),
  catalog_hub: a('catalog', 'Catalog hub', 'Katalog merkezi'),
  catalog_issue: a('catalog', 'Catalog issues', 'Katalog sorunları'),
  master_catalog: a('catalog', 'Master catalog', 'Ana katalog'),
  product_variant: a('catalog', 'Product variants', 'Ürün varyantları'),
  variation_slot: a('catalog', 'Variation axes', 'Varyasyon eksenleri'),
  variation_value: a('catalog', 'Variation values', 'Varyasyon değerleri'),
  sku: a('catalog', 'SKUs', 'SKU\'lar'),
  vendor_code: a('catalog', 'Vendor codes', 'Tedarikçi kodları'),
  seller_product_code: a('catalog', 'Seller product codes', 'Satıcı ürün kodları'),
  seller_store_sku: a('catalog', 'Seller store SKUs', 'Satıcı mağaza SKU\'ları'),
  unmapped_skus: a('catalog', 'Unmapped SKUs', 'Eşlenmemiş SKU\'lar'),
  assortment: a('catalog', 'Assortment', 'Ürün yelpazesi'),
  design_asset: a('catalog', 'Design assets', 'Tasarım dosyaları'),
  design_studio: a('catalog', 'Design studio', 'Tasarım stüdyosu'),
  font_library: a('catalog', 'Font library', 'Yazı tipi kütüphanesi'),
  scene_preset: a('catalog', 'Scene presets', 'Sahne şablonları'),
  scene_wizard_option: a('catalog', 'Scene wizard options', 'Sahne sihirbazı seçenekleri'),
  personalization: a('catalog', 'Personalization', 'Kişiselleştirme'),
  listing: a('catalog', 'Listings', 'İlanlar'),
  document_studio: a('catalog', 'Document studio', 'Doküman stüdyosu'),
  // Orders & returns
  orders: a('orders', 'Orders', 'Siparişler'),
  returns: a('orders', 'Returns', 'İadeler'),
  return_credit: a('orders', 'Return credits', 'İade kredileri'),
  restocking_fee: a('orders', 'Restocking fees', 'Yeniden stoklama ücreti'),
  replacement_evidence: a('orders', 'Replacement evidence', 'Değişim kanıtları'),
  packing_slip: a('orders', 'Packing slips', 'İrsaliyeler'),
  dispute: a('orders', 'Disputes', 'Anlaşmazlıklar'),
  review: a('orders', 'Reviews', 'Yorumlar'),
  routing: a('orders', 'Order routing', 'Sipariş yönlendirme'),
  routing_decisions: a('orders', 'Routing decisions', 'Yönlendirme kararları'),
  vendor_replacement: a('orders', 'Vendor replacements', 'Tedarikçi değişimleri'),
  // Stores & marketplaces
  store_integration: a('stores', 'Store connections', 'Mağaza bağlantıları'),
  store_publish: a('stores', 'Store publishing', 'Mağazada yayınlama'),
  etsy_listing_push: a('stores', 'Etsy listing push', 'Etsy ilan gönderimi'),
  etsy_push: a('stores', 'Etsy push', 'Etsy gönderimi'),
  ebay_listing_publish: a('stores', 'eBay listing publish', 'eBay ilan yayınlama'),
  marketplace_credential: a('stores', 'Marketplace credentials', 'Pazar yeri kimlik bilgileri'),
  org_shipstation_credential: a('stores', 'ShipStation API key', 'ShipStation API anahtarı'),
  webhook_delivery: a('stores', 'Webhook deliveries', 'Webhook teslimatları'),
  webhook_subscriber: a('stores', 'Webhook subscribers', 'Webhook aboneleri'),
  fe_transition: a('stores', 'Fulfillment ecosystem switch', 'Karşılama ekosistemi geçişi'),
  fulfillment_ecosystem: a('stores', 'Fulfillment ecosystems', 'Karşılama ekosistemleri'),
  // Pricing & sales
  pricing: a('pricing', 'Pricing', 'Fiyatlandırma'),
  pricing_hub: a('pricing', 'Pricing Hub', 'Fiyat merkezi'),
  pricing_matrix: a('pricing', 'Pricing matrix', 'Fiyat matrisi'),
  pricing_rules: a('pricing', 'Pricing rules', 'Fiyat kuralları'),
  b2b: a('pricing', 'B2B', 'B2B'),
  fx: a('pricing', 'Exchange rates', 'Döviz kurları'),
  sales_goal: a('pricing', 'Sales goals', 'Satış hedefleri'),
  referral: a('pricing', 'Referral program', 'Referans programı'),
  // Purchasing & suppliers
  po: a('purchasing', 'Purchase orders', 'Satın alma siparişleri'),
  service_provider: a('purchasing', 'Suppliers', 'Tedarikçiler'),
  vendor_update: a('purchasing', 'Supplier updates', 'Tedarikçi güncellemeleri'),
  company_registration: a('purchasing', 'Company registrations', 'Şirket kayıtları'),
  onboarding: a('purchasing', 'Onboarding invitations', 'Katılım davetleri'),
  leads: a('purchasing', 'Leads and scoreboard', 'Adaylar ve skor tablosu'),
  agreement: a('purchasing', 'Agreements', 'Sözleşmeler'),
  // Warehouse & inventory
  inventory: a('inventory', 'Inventory', 'Stok'),
  warehouses: a('inventory', 'Warehouses', 'Depolar'),
  warehouse: a('inventory', 'Warehouse floor', 'Depo operasyonu'),
  allocation: a('inventory', 'Stock allocation', 'Stok ayırma'),
  allocation_escrow: a('inventory', 'Allocation escrow', 'Ayırma emaneti'),
  escrow_ttl: a('inventory', 'Escrow expiry', 'Emanet süresi'),
  custody: a('inventory', 'Custody chain', 'Emanet zinciri'),
  wo: a('inventory', 'Work orders', 'İş emirleri'),
  // Shipping
  ship: a('shipping', 'Shipping labels', 'Gönderi etiketleri'),
  carrier_credential: a('shipping', 'Carrier accounts', 'Taşıyıcı hesapları'),
  // Finance & payments
  finance: a('finance', 'Finance', 'Finans'),
  bank: a('finance', 'Bank', 'Banka'),
  banking: a('finance', 'Bank accounts', 'Banka hesapları'),
  payout: a('finance', 'Payouts', 'Ödemeler'),
  settlement: a('finance', 'Settlements', 'Hesap kesimleri'),
  payment_gateway: a('finance', 'Payment gateways', 'Ödeme altyapıları'),
  // Messaging & notifications
  messaging: a('messaging', 'Messaging', 'Mesajlaşma'),
  problem_report: a('messaging', 'Problem reports', 'Sorun bildirimleri'),
  notif: a('messaging', 'Notification engine', 'Bildirim motoru'),
  notif_subscription: a('messaging', 'Notification subscriptions', 'Bildirim abonelikleri'),
  notif_template: a('messaging', 'Notification templates', 'Bildirim şablonları'),
  notifications: a('messaging', 'Notifications', 'Bildirimler'),
  special_days: a('messaging', 'Special days', 'Özel günler'),
  // Files & Drive
  drive: a('drive', 'Drive', 'Drive'),
  // Team & people
  team: a('team', 'Team', 'Ekip'),
  staff_task: a('team', 'Tasks', 'Görevler'),
  memberships: a('team', 'Memberships', 'Üyelikler'),
  pto: a('team', 'Time off', 'İzinler'),
  schedule: a('team', 'Schedules', 'Vardiyalar'),
  monitoring: a('team', 'Employee tracking', 'Çalışan takibi'),
  workspace: a('team', 'Workspace', 'Çalışma alanı'),
  // Servers & remote access
  remote_access: a('remote', 'Remote access', 'Uzak erişim'),
  resource: a('remote', 'Resources', 'Kaynaklar'),
  resource_permission: a('remote', 'Resource permissions', 'Kaynak izinleri'),
  // Platform & governance
  platform: a('platform', 'Platform administration', 'Platform yönetimi'),
  admin_config: a('platform', 'Admin switches', 'Yönetim anahtarları'),
  media_generation_credential: a(
    'platform',
    'Media generation credentials',
    'Medya üretimi kimlik bilgileri',
  ),
  research_tool_credential: a(
    'platform',
    'Research tool keys (Alura)',
    'Araştırma aracı anahtarları (Alura)',
  ),
  push_provider_credential: a(
    'platform',
    'Push provider keys (Apple)',
    'Bildirim sağlayıcı anahtarları (Apple)',
  ),
  org: a('platform', 'Organizations', 'Organizasyonlar'),
  policy: a('platform', 'Policies', 'Politikalar'),
  policy_override: a('platform', 'Policy overrides', 'Politika istisnaları'),
  rule: a('platform', 'Rules', 'Kurallar'),
  activity: a('platform', 'Activity log', 'Etkinlik kaydı'),
  reports: a('platform', 'Reports', 'Raporlar'),
  executive: a('platform', 'Executive dashboard', 'Yönetici panosu'),
  exit_plan: a('platform', 'Exit plan', 'Exit planı'),
  self: a('platform', 'Own profile', 'Kendi profili'),
  settings: a('platform', 'Organization settings', 'Organizasyon ayarları'),
}

const WORDS: Record<string, Text> = {
  view: { en: 'View', tr: 'Görüntüle' },
  use: { en: 'Use', tr: 'Kullan' },
  read: { en: 'View', tr: 'Görüntüle' },
  edit: { en: 'Edit', tr: 'Düzenle' },
  write: { en: 'Edit', tr: 'Düzenle' },
  update: { en: 'Edit', tr: 'Düzenle' },
  manage: { en: 'Manage', tr: 'Yönet' },
  create: { en: 'Create', tr: 'Oluştur' },
  delete: { en: 'Delete', tr: 'Sil' },
  approve: { en: 'Approve', tr: 'Onayla' },
  reject: { en: 'Reject', tr: 'Reddet' },
  admin: { en: 'Administer', tr: 'Yönet (tam yetki)' },
  export: { en: 'Export', tr: 'Dışa aktar' },
  upload: { en: 'Upload', tr: 'Yükle' },
  share: { en: 'Share', tr: 'Paylaş' },
  retry: { en: 'Retry', tr: 'Yeniden dene' },
  request: { en: 'Request', tr: 'Talep et' },
  decide: { en: 'Decide', tr: 'Karar ver' },
  submit: { en: 'Submit', tr: 'Gönder' },
  receive: { en: 'Receive', tr: 'Teslim al' },
  cancel: { en: 'Cancel', tr: 'İptal et' },
  close: { en: 'Close', tr: 'Kapat' },
  invite: { en: 'Invite', tr: 'Davet et' },
  revoke: { en: 'Revoke', tr: 'Geri al' },
  generate: { en: 'Generate', tr: 'Üret' },
  regenerate: { en: 'Regenerate', tr: 'Yeniden üret' },
  connect: { en: 'Connect', tr: 'Bağlan' },
  retire: { en: 'Retire', tr: 'Emekliye ayır' },
  scan: { en: 'Scan', tr: 'Tara' },
  purchase: { en: 'Purchase', tr: 'Satın al' },
  void: { en: 'Void', tr: 'İptal et' },
  sync: { en: 'Sync', tr: 'Senkronla' },
  issue: { en: 'Issue', tr: 'Düzenle ve ver' },
  report: { en: 'Report', tr: 'Bildir' },
  verify: { en: 'Verify', tr: 'Doğrula' },
  transfer: { en: 'Transfer money', tr: 'Para transferi' },
}

function humanize(segment: string): string {
  const text = segment.replaceAll('_', ' ')
  return text.charAt(0).toUpperCase() + text.slice(1)
}

function words(segment: string, locale: Locale): string {
  return WORDS[segment]?.[locale] ?? humanize(segment)
}

type CatalogEntry = { id: string; area: string; areaLabel: string; label: string }
export type CatalogGroup = { id: CapabilityGroupId; label: string; areas: { area: string; label: string; entries: CatalogEntry[] }[]; ids: string[] }

/** Readable action name for one id: `ship.label.void` → "Label · Void". */
export function capabilityActionLabel(id: string, locale: Locale): string {
  const [, ...rest] = id.split('.')
  if (rest.length === 0) return words(id, locale)
  return rest.map((segment, index) => (index === rest.length - 1 ? words(segment, locale) : humanize(segment))).join(' · ')
}

/** Group the given ids (one role's base set) into ordered, labelled groups. */
export function buildCapabilityGroups(ids: readonly string[], locale: Locale): CatalogGroup[] {
  const byGroup = new Map<CapabilityGroupId, Map<string, CatalogEntry[]>>()
  for (const id of ids) {
    const area = id.split('.')[0]
    const known = AREAS[area]
    const group = known?.group ?? 'other'
    const areaLabel = known?.label[locale] ?? humanize(area)
    const areas = byGroup.get(group) ?? new Map<string, CatalogEntry[]>()
    areas.set(area, [...(areas.get(area) ?? []), { id, area, areaLabel, label: capabilityActionLabel(id, locale) }])
    byGroup.set(group, areas)
  }
  return CAPABILITY_GROUPS.flatMap((group) => {
    const areas = byGroup.get(group.id)
    if (!areas) return []
    const ordered = [...areas.entries()]
      .map(([area, entries]) => ({ area, label: entries[0].areaLabel, entries: [...entries].sort((x, y) => x.id.localeCompare(y.id)) }))
      .sort((x, y) => x.label.localeCompare(y.label, locale))
    return [{ id: group.id, label: group.label[locale], areas: ordered, ids: ordered.flatMap((item) => item.entries.map((entry) => entry.id)) }]
  })
}
